"use client";
import React from "react";
import {Icon} from "@iconify/react";
import {cn} from "@heroui/react";
import { Link } from "react-router";
import Typography from "../typography/Typography";
import { getButtonVariantColorsByUserRole } from "../../theme/colors";
export enum SidebarItemType {
  Nest = "nest",
}
export type SidebarItem = {
  key: string;
  title: string;
  icon?: string;
  href?: string;
  type?: SidebarItemType.Nest;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  items?: SidebarItem[];
  className?: string;
};
export type SidebarProps = {
  items: SidebarItem[];
  iconClassName?: string;
  className?: string;
  selectedKey?: string;
  isPending?: boolean;
  startTransition?: (callback: () => void) => void;
  userRole?: string;
};
const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      items,
      iconClassName,
      className,
      selectedKey,
      isPending,
      startTransition,
      userRole,
    },
    ref,
  ) => {
    const [internalIsPending, internalStartTransition] = React.useTransition();
    const currentIsPending = isPending || internalIsPending;
    const primaryColors = React.useMemo(() => getButtonVariantColorsByUserRole('primary', userRole), [userRole]);
    const renderItem = React.useCallback((item: SidebarItem) => {
      if (!item.href) return null;
      const isSelected = selectedKey === item.key;
      return (
        <li key={item.key}>
          <Link
            to={item.href}
            className={cn(
              "flex items-center gap-[6px] px-[16px] py-[12px] min-h-[44px] rounded-large",
              "text-[14px] font-semibold",
              isSelected ? null : "hover:bg-default-300/20 dark:hover:bg-default-200/40",
              "transition-colors duration-150 ease-in-out",
              "w-full",
              isSelected
                ? `text-white`
                : "text-[#71717A]"
            )}
            style={isSelected ? { backgroundColor: primaryColors.background } : undefined}
          >
            {item.icon ? (
              <Icon
                className={cn(
                  "transition-colors duration-150",
                  iconClassName,
                  isSelected 
                    ? "!text-white" 
                    : "text-default-400",
                )}
                icon={item.icon}
                width={24}
                aria-label={`${item.title} иконка`}
              />
            ) : (
              <div className={cn(
                "transition-colors duration-150",
                isSelected && "[&_svg]:!text-white [&_.text-default-400]:!text-white"
              )}>
                {item.startContent}
              </div>
            )}
            <span className="flex-1">{item.title}</span>
            <div className={cn(
              "transition-colors duration-150",
              isSelected && "[&_.sidebar-chip]:!text-white [&_.sidebar-chip]:!bg-white/20"
            )}>
              {item.endContent}
            </div>
          </Link>
        </li>
      );
    }, [iconClassName, selectedKey]);

    const renderSection = React.useCallback((item: SidebarItem) => {
      if (!item.items || item.items.length === 0) {
        return renderItem(item);
      }
      return (
        <li key={item.key} >
          <div className="mb-[8px]">
            <Typography variant="label3" className="text-[#71717A]">
              {item.title}
            </Typography>
          </div>
          <ul className="flex flex-col gap-0">
            {item.items.map(renderItem)}
          </ul>
        </li>
      );
    }, [renderItem]);

    return (
      <nav
        ref={ref}
        className={cn(
          "w-full",
          className,
          {
            "opacity-50 pointer-events-none": currentIsPending
          }
        )}
        aria-label="Главное навигационное меню"
      >
        <ul className="flex flex-col gap-[32px]">
          {items.map(renderSection)}
        </ul>
      </nav>
    );
  },
);

Sidebar.displayName = "Sidebar";

const MemoizedSidebar = React.memo(Sidebar, (prevProps, nextProps) => {
  return (
    prevProps.selectedKey === nextProps.selectedKey &&
    prevProps.isPending === nextProps.isPending &&
    prevProps.items === nextProps.items
  );
});

export default MemoizedSidebar;
